'use client';

import { Divider, MenuItem, Select, Stack, Typography } from '@mui/material';

import { inlineDropdownStyle } from '@/components/conditions/inlineDropdownStyle';

import { ToolbarPanel } from './toolbarPanel';

const ROW_HORIZONTAL_PADDING = '24px';

/** What a table brings to its settings panel beyond the page size every table shares. */
export interface TableSettingsSource {
  /** The table's own settings rows, listed above the page size. */
  content?: React.ReactNode;
}

/** One setting: its name on the left, the control that changes it on the right. */
export const TableSettingsRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Stack
    direction='row'
    sx={{
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `0px ${ROW_HORIZONTAL_PADDING}`,
    }}
  >
    <Typography variant='subtitle1'>{label}</Typography>
    {children}
  </Stack>
);

/**
 * The toolbar's settings button and the panel it opens, holding the table-wide
 * controls that aren't per-column. It is the only place page size can be changed
 * — the pagination bar has no select of its own — and the bar is the only thing
 * that renders it, from the `pageSizeControl` its caller hands over.
 */
export const TableSettings = ({
  settings,
  pageSize,
  rowsPerPageOptions,
  onPageSizeChange,
}: {
  settings: TableSettingsSource;
  pageSize: number;
  rowsPerPageOptions: number[];
  onPageSizeChange: (pageSize: number) => void;
}) => (
  <ToolbarPanel
    icon='material-symbols-settings-outline-rounded'
    title='Settings'
    testId='table-settings'
    contentSx={{ padding: '12px 0px 20px' }}
  >
    <Stack sx={{ gap: '12px' }}>
      {settings.content}
      {!!settings.content && <Divider />}
      <TableSettingsRow label='Items per page'>
        <Select
          data-testid='table-settings-page-size'
          inputProps={{ 'aria-label': 'Items per page' }}
          value={pageSize}
          onChange={(event) => onPageSizeChange(event.target.value)}
          sx={inlineDropdownStyle}
          IconComponent={() => (
            <i className='material-symbols-arrow-drop-down-rounded' />
          )}
          renderValue={(value) => (
            <Typography variant='subtitle1Strong'>{value}</Typography>
          )}
        >
          {rowsPerPageOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </TableSettingsRow>
    </Stack>
  </ToolbarPanel>
);
